<template>
  <div>
    <page-content-header
      :item="item"
    />
    <b-container>
      <b-row>
        <b-col>
          <page-content-creator
            :itemid="itemid"
            pos="top"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <node-element-local-tasks
            v-if="itemid"
            :itemid="itemid"
          />
          <div
            v-if="notpublished"
            class="messages warning"
          >
            Not published
          </div>
        </b-col>
      </b-row>
    </b-container>
    <section class="page-body">
      <div
        v-for="(slice, index) in slices"
        :key="index"
        class="slice"
      >
        <b-container v-if="sliceContained(slice.data)">
          <b-row class="justify-content-center">
            <b-col v-bind="getSliceWidth(slice.data)">
              <component
                :is="getSliceComponent(slice)"
                :slice="slice"
                :pageid="itemid"
              />
            </b-col>
          </b-row>
        </b-container>
        <component
          :is="getSliceComponent(slice)"
          v-else
          :slice="slice"
          :pageid="itemid"
        />
      </div>
    </section>
    <b-container>
      <b-row>
        <b-col>
          <page-content-creator
            :itemid="itemid"
            pos="bottom"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>
<script>
import { asyncLoader, getPreloadImageUrlPromises, pageMixin } from '../../special-pages-common'

import sliceTypeWysiwyg from '../slice-types/wysiwyg'
import sliceTypeContentCreator from '../slice-types/content-creator'
import sliceTypeAdvancedWysiwyg from '../slice-types/advanced-wysiwyg'
import sliceTypeFeatures from '../slice-types/features'
import sliceTypeForm from '../slice-types/form'
import sliceTypeHighlights from '../slice-types/highlights'
import sliceTypeGallery from '../slice-types/gallery'

function preloadImageUrls({ store, route, router }) {
  if (!route || !store) {
    return Promise.resolve(false)
  }
  applogger.debug('sp-constructed: image url loading started')
  return store.dispatch('node/load', { nid: route.path })
    .then(item => {
      return getPreloadImageUrlPromises(
        store,
        ['banner'],
        [[item.field_f_content_header_img]]
      )
    })
    .then(() => {
      applogger.debug('sp-constructed: image url loading complete')
    })
    .catch(err => {
      applogger.error('sp-constructed: image url loading failed')
    })
}

export default {
  name: 'page-content-type-sp-constructed',
  components: {
    sliceTypeAdvancedWysiwyg,
    sliceTypeWysiwyg,
    sliceTypeContentCreator,
    sliceTypeFeatures,
    sliceTypeForm,
    sliceTypeHighlights,
    sliceTypeGallery
  },
  mixins: [
    pageMixin
  ],
  asyncData: (args) => {
    return args.store.dispatch('promisePool', {
      name: `page-content-type_sp_constructed--${args.route.path}`,
      resolve: () => preloadImageUrls(args)
        .then(() => asyncLoader(args))
    })
  },
  computed: {
    schema() {
      return JSON.parse(this.item.construct_data)
    },
    slices() {
      return this.schema.slices
    }
  },
  methods: {
    sliceContained(slice) {
      return slice.restrictedWidthChoice !== 'none'
    },
    getSliceComponent(slice) {
      return `slice-type-${slice.type}`
    },
    getSliceWidth(slice) {
      const sliceWidthRestrictionAmount = (slice.restrictedWidthChoice) ?? 'standard'
      const columnRestrictions = {
        standard: { cols: '12' },
        small: { cols: '12', sm: '12', md: '10' },
        medium: { cols: '12', sm: '12', md: '10', lg: '9', xl: '8' },
        large: { cols: '12', sm: '12', md: '10', lg: '8', xl: '6' }
      }

      return columnRestrictions[sliceWidthRestrictionAmount]
    }
  }
}
</script>

<style scoped lang="scss">
  @import '~@scss/custom.scss';
  //scale
  @mixin transform-scale($x:1, $y:1) {
    -webkit-transform: scale($x, $y);
    -moz-transform: scale($x, $y);
    -o-transform: scale($x, $y);
    -ms-transform: scale($x, $y);
    transform: scale($x, $y);
  }

  //background ratio
  @mixin responsive-ratio($x,$y, $pseudo: false) {
    $padding: unquote(( $y / $x ) * 100 + '%');
    @if $pseudo {
      &:before {
        @include pseudo($pos: relative);
        width: 100%;
        padding-bottom: $padding;
      }
    } @else {
      padding-bottom: $padding;
    }
  }

 ::v-deep .timeline-panel {
    .a-image {
      img {
        @include transform-scale(1, 1);
        @include transition(transform .75s);
      }
    }

    &:hover {
      .a-image {
        img {
          @include transform-scale(1.1, 1.1);
        }
      }
    }
  }

  .timeline-section {
    padding-top: 3em;
    padding-bottom: 3em;
    background: var(--main-bg-color);
  }

  .timeline-header {

    margin-bottom: 4em;
    text-align: center;
  }

  .timeline {
    list-style: none;
    position: relative;
    padding: 3em 0em;
    margin: 0;
  }

  .timeline:before {
    position: absolute;
    background: var(--main-border-color);
    bottom: 0;
    left: 50%;
    top: 0;
    content: "";
    width: 3px;
    margin-left: -1.5px;
    opacity: .25;
    @include media-breakpoint-up(md) {
      opacity: 1;
    }

  }

  .timeline-top, .timeline-bottom {
    background: var(--main-border-color);
    position: absolute;
    height: 3px;
    width: 50px;
    display: block;
    content: "";
    bottom: 0;
    left: 50%;
    margin-left: -25px;
  }

  .timeline-top {
    top: 0;
  }

  .timeline-bottom {
    bottom: 0;
  }

  .timeline {
    .timeline-step {
      //position: absolute;
      padding-bottom: 1.5rem;
      z-index: 2;
      line-height: 1;
      display: flex;
      flex-direction: column;
      @include media-breakpoint-down(md) {
        text-align: center;
        left: 0;
        right: 0;
      }

      .larger {
        font-size: 3em;
        margin-bottom: .5rem;
      }

      .smaller {
        font-size: 1.25em;
      }
    }
  }

  .timeline-body {
    padding: 1em 0em;
    z-index: 2;
    position: relative;
    text-align: center;
    @include media-breakpoint-up(md) {
      text-align: left;
    }

    .title {
      font-size: 2.3em;
    }

    .timeline-action {
      a {
        font-size: 1.1em;
      }
    }
  }

  .timeline > li {
    margin-bottom: 1.5em;
    position: relative;
  }

  .timeline > li:after,
  .timeline > li:before {
    display: table;
    content: "";
  }

  .timeline > li:after {
    clear: both;
  }

  .timeline > li {
    &:nth-child(odd) {
      .timeline-panel {
        @include media-breakpoint-up(md) {
          padding-right: 2.5em;
        }
      }

      @include media-breakpoint-up(md) {
        .timeline-step {
          right: 0;
          padding-right: 3.5em;
          text-align: right;
        }
      }

      @include media-breakpoint-up(md) {

        .timeline-badge-container {
          right: 0;

          .timeline-badge {
            right: -.5em;
          }
        }
      }
    }

    &:nth-child(even) {
      .timeline-panel {
        @include media-breakpoint-up(md) {
          padding-left: 2.5em;
        }
      }

      @include media-breakpoint-up(md) {

        .timeline-badge-container {
          left: 0;

          .timeline-badge {
            left: -.5em;
          }
        }
      }
      @include media-breakpoint-up(md) {
        .timeline-step {
          left: 0;
          padding-left: 3.5em;
          text-align: left;
        }
      }
    }

    .timeline-panel {
      z-index: 5;
      width: 100%;
      @include media-breakpoint-up(md) {
        min-height: 25em;
        width: 50%;
      }
      position: relative;
      transition: .3s ease-in-out;

      &:hover {
        -webkit-transform: translateY(-.75em);
        transform: translateY(-.75em);
      }

      &.text-overlay {
        .timeline-body {
          position: absolute;
          bottom: 0;
        }
      }
    }

  }

  .timeline > li .timeline-panel-inner {
    position: relative;
    display: flex;
    flex-direction: column;
    @include media-breakpoint-down(sm) {
      padding-top: 5em;
    }
  }

  .timeline > li > .timeline-panel .timeline-preview img {
    width: 100%;
  }

  .timeline > li .timeline-panel:hover {
    -webkit-transform: translateY(-.75em);
    transform: translateY(-.75em);
  }

  .timeline > li {
    display: flex;
  }

  .timeline > li:nth-child(even) {
    @include media-breakpoint-up(md) {
      justify-content: flex-end;
      margin-top: -25em;
      padding-top: 5em;
    }
  }

  .timeline-badge-container {

    @include media-breakpoint-down(sm) {
      .timeline-badge {
        left: 0;
        right: 0;
        position: absolute;
        display: flex;
        background: var(--main-border-color);
        border-radius: 100%;
        height: 1em;
        width: 1em;
        z-index: 1;
        margin: 0 auto;
      }
    }
    @include media-breakpoint-up(md) {
      display: flex;
      top: 3em;
      position: absolute;
      right: 0;
      justify-content: flex-end;
      align-items: center;
      background: var(--main-border-color);
      height: 3px;
      width: 5em;

      .timeline-badge {
        position: absolute;
        display: flex;
        background: var(--main-border-color);
        border-radius: 100%;
        height: 1em;
        width: 1em;
        z-index: 1;
      }
    }
  }
</style>
